import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import SelectField from '@pages/Admin/CourseSettings/components/SelectField/SelectField'
import { InputNew } from '@UI/InputNew'
import { directionAPI, directionGroupApi, massActionsApi } from '@/api'
import { DatePicker, Select, Tooltip } from 'antd'
import { useAddAlert } from '@/hooks/useAddAlert'
import { formateName } from '@/helpers/formateName'
import { CheckBoxLabel } from '@UI/CheckBox'
import CreateLearningGroup from '@pages/Admin/CourseSettings/modals/CreateLearningGroup/CreateLearningGroup'
import classes from './purchase.module.css'
import type { Dayjs } from 'dayjs'
import { PurchaseHandle, PurchaseHandleSaveFnParams } from '.'
import { useSelector } from 'react-redux'

interface SetCourseModalBodyProps {
  excludedDirectionIds: number[]
}

type DateStateType = {
  dateString: string | string[]
  date: Dayjs | null
} | null

export type ICustomCourse = Omit<ICourse, 'seniorSeminarians'>

type GroupOption = { label: string; value: number; max_students?: number | null; student_count?: number | null; }

const defaultManualPaymentTypes = ['Оплата наличными', 'Оплата по счету']

const parseManualPaymentTypes = (value?: string) => {
  if (!value) return defaultManualPaymentTypes
  try {
    const parsed = JSON.parse(value)
    if (!Array.isArray(parsed)) return defaultManualPaymentTypes
    const values = parsed
      .map((item) => (typeof item === 'string' ? item : item?.name))
      .map((item) => String(item ?? '').trim())
      .filter(Boolean)
    return [...new Set(values)]
  } catch (e) {
    return defaultManualPaymentTypes
  }
}

const SetCourseModalBody = forwardRef<PurchaseHandle, SetCourseModalBodyProps>(
  ({ excludedDirectionIds }, ref) => {
    const addAlert = useAddAlert()
    const settings = useSelector((store: any) => store.settings)
    const isUniversalBalance = settings?.isUniversalBalance === '1'
    const allowSameCourseInDifferentGroups =
      settings?.allowStudentToEnrollSameCourseInDifferentGroups === '1'

    const [coursesList, setCoursesList] = useState<ICustomCourse[]>([])
    const [selectedCourse, setSelectedCourse] = useState<ICustomCourse | null>(
      null,
    )
    const [curseGroups, setCourseGroups] = useState<GroupOption[]>([])
    const [selectedGroup, setSelectedGroup] = useState<number | null>(null)
    const [selectedTariff, setSelectedTariff] = useState<ICourseTariff | null>(
      null,
    )
    const [isNewPaymentForCourse, setNewPaymentForCourse] = useState(false)
    const [paymentAmount, setPaymentAmount] = useState(0)
    const [manualPaymentType, setManualPaymentType] = useState<string | null>(null)
    const isManualPaymentTypeSelectionEnabled = useSelector(
      (store: any) => store.settings.isManualPaymentTypeSelectionEnabled === '1',
    )
    const manualPaymentTypes = parseManualPaymentTypes(
      useSelector((store: any) => store.settings.custom.manualPaymentTypes),
    )
    const [attachedGroup, setAttachedGroup] = useState<GroupOption | null>(null)
    const [createGroupModal, setCreateGroupModal] = useState(false)

    useEffect(() => {
      directionAPI
        .getAll({
          filter:
            !allowSameCourseInDifferentGroups && excludedDirectionIds?.length > 0
              ? [`[direction_id][nin][]=${excludedDirectionIds}`]
              : [],
          expand: ['directionTariffs'],
        })
        .then((data) => {
          setCoursesList(
            (data?.data?.data as ICustomCourse[])?.filter((direction) =>
              direction?.directionTariffs?.some(
                (tariff) => tariff.type === 'free' || tariff.type === 'month',
              ),
            ) ?? [],
          )
        })
    }, [excludedDirectionIds, allowSameCourseInDifferentGroups])

    useEffect(() => {
      setSelectedTariff(null)
      setCourseGroups([])
      setSelectedGroup(null)
      setAttachedGroup(null)
      if (selectedCourse === null) return
      directionGroupApi
        .get({
          filter: [`[direction_id][eq]=${selectedCourse.id}`],
          expand: ['teacher'],
        })
        .then((res) => {
          setCourseGroups(
            res.data?.map(({ name, id: value, teacher, max_students, student_count }: any) => ({
              label: `${name} - ${formateName(teacher?.name, 'F I')}`,
              value,
              max_students,
              student_count
            })),
          )
        })
    }, [selectedCourse])

    const [paidFromDate, setPaidFromDate] = useState<DateStateType>(null)
    const [paidBeforeDate, setPaidBeforeDate] = useState<DateStateType>(null)

    const onChangePaidFrom = (
      date: Dayjs | null,
      dateString: string | string[],
    ) => {
      setPaidFromDate({
        dateString: dateString,
        date: date,
      })
    }

    const onChangePaidBeforeDate = (
      date: Dayjs | null,
      dateString: string | string[],
    ) => {
      setPaidBeforeDate({
        dateString: dateString,
        date: date,
      })
    }

    const options = useMemo(() => {
      return selectedCourse?.directionTariffs
        ?.filter((tariff) =>
          [
            'free',
            'month',
            'stream_custom_period',
            'stream_custom_days',
            'stream_custom_weeks',
          ].includes(tariff?.type),
        )
        .map(({ name: label, id: value }) => ({
          label,
          value,
        }))
    }, [selectedCourse])

    const filterSort = useCallback((optionA: any, optionB: any) => {
      return (optionA?.label ?? '')
        .toLowerCase()
        .localeCompare((optionB?.label ?? '').toLowerCase())
    }, [])

    const handleChangeTariff = useCallback(
      (e: number) => {
        const selectedTariff = selectedCourse?.directionTariffs?.find(
          (item) => item?.id === e,
        )
        setSelectedTariff(selectedTariff || null)
        const attachedGroup = curseGroups?.find(
          (item) => item?.value === selectedTariff?.attached_groups,
        )
        setAttachedGroup(attachedGroup ?? null)
        setSelectedGroup(attachedGroup?.value ?? null)
      },
      [selectedCourse, setSelectedTariff, curseGroups],
    )

    const handleChangeGroup = (id: number) => {
      if (attachedGroup) return
      setSelectedGroup(id)
    }

    useImperativeHandle(ref, () => ({
      handleSave,
    }))

    const handleSave = async (
      props?: PurchaseHandleSaveFnParams,
    ): Promise<'success' | 'error'> => {
      if (!selectedCourse?.id) {
        addAlert({
          title: 'Ошибка',
          text: 'Нужно выбрать курс!',
          type: 'error',
        })
        return 'error'
      }
      if (!selectedTariff?.id) {
        addAlert({
          title: 'Ошибка',
          text: 'Нужно выбрать тариф!',
          type: 'error',
        })
        return 'error'
      }
      if (!selectedGroup) { 
        addAlert({
          title: 'Ошибка',
          text: 'Нужно выбрать группу!',
          type: 'error',
        })
        return 'error'
      }
       
      const currentGroup = curseGroups?.find((el) => el.value === selectedGroup)
      if(!!currentGroup?.max_students && !!currentGroup?.student_count && !attachedGroup){
        if(Number(currentGroup?.student_count) >= Number(currentGroup?.max_students)){
          addAlert({
            type: 'error',
            title: 'Ошибка',
            text: 'В выбранную группу нельзя добавить учащегося, она переполнена',
          })
          return 'error'
        }
      }
      
      if (
        selectedTariff?.type !== 'free' &&
        (!paidFromDate?.date || !paidBeforeDate?.date)
      ) {
        addAlert({
          title: 'Ошибка',
          text: 'Нужно выбрать дату начала и конца курса!',
          type: 'error',
        })
        return 'error'
      }

      const data = new FormData()
      data.append(
        'users',
        JSON.stringify((props?.users || []).map((user) => user.id)),
      )
      data.append('course_id', String(selectedCourse?.id))
      data.append('tariff_id', String(selectedTariff?.id))
      data.append('group_id', String(selectedGroup))

      if (selectedTariff?.type === 'month') {
        data.append('paid_from', String(paidFromDate?.dateString || ''))
        data.append('paid_before', String(paidBeforeDate?.dateString || ''))
        data.append('new_payment', String(isNewPaymentForCourse))
        data.append('payment_amount', String(paymentAmount))
        if (manualPaymentType) {
          data.append('manual_payment_type', manualPaymentType)
        }
      }

      if (props?.paymentFrom !== undefined) {
        data.append(
          'balance_action',
          String(props.paymentFrom === 'crm' ? 7 : ''),
        )
      }

      if (props?.othersParams !== undefined) {
        Object.entries(props.othersParams).forEach(([key, value]) => {
          data.append(key, String(value))
        })
      }

      const handleResponse = (res: any): 'success' | 'error' => {
        if (res.data.status !== 'success') {
          addAlert({
            title: 'Ошибка',
            text: 'Упс! Ошибка. Попробуйте ещё раз или обратитесь к администратору',
            type: 'error',
          })
          return 'error'
        } else {
          addAlert({
            title: 'Добавление прошло успешно!',
          })
          return 'success'
        }
      }

      try {
        const res = await massActionsApi.addCourses(data)
        return handleResponse(res)
      } catch (error) {
        addAlert({
          title: 'Ошибка',
          text: 'Что-то пошло не так. Пожалуйста, попробуйте ещё раз.',
          type: 'error',
        })
        return 'error'
      }
    }

    const onGroupCreated = (group: any) => {
      setCreateGroupModal(false)
      setCourseGroups((prev) => [
        ...prev,
        { value: group?.id?.toString(), label: group?.name },
      ])
    }

    return (
      <>
        <div className={classes.formWrapper}>
          <div className={classes.contentBodyWrapper}>
            <div className={classes.inputWithLabel}>
              <div className={classes.inputLabel}>Курс</div>
              <Select
                showSearch
                className={classes.select}
                placeholder="Выберите курс"
                optionFilterProp="label"
                value={selectedCourse?.id}
                onChange={(e) =>
                  setSelectedCourse(
                    coursesList.find((item) => {
                      return item?.id === e
                    }) || null,
                  )
                }
                filterSort={filterSort}
                options={coursesList?.map(({ name: label, id: value }) => ({
                  label,
                  value,
                }))}
              />
            </div>
            <div className={classes.inputWithLabel}>
              <div className={classes.inputLabel}>Тариф</div>
              <Select
                showSearch
                className={classes.select}
                placeholder="Выберите тариф"
                optionFilterProp="label"
                value={selectedTariff?.id}
                onChange={handleChangeTariff}
                filterSort={filterSort}
                options={options}
              />
            </div>
            {selectedTariff !== null && (
              <div className={classes.inputWithLabel}>
                <div className={classes.inputLabel}>Выберите группу</div>
                <SelectField
                  placeholder="Название"
                  value={selectedGroup ? String(selectedGroup) : null}
                  className={classes.selectField}
                  popupClassName={classes.selectFieldPopup}
                  onChange={(e) => {
                    if (e === 'create_group') {
                      setCreateGroupModal(true)
                    } else if (typeof e === 'string') {
                      handleChangeGroup(Number(e))
                    }
                  }}
                  // @ts-ignore
                  options={[
                    { label: 'Создать группу', value: 'create_group' },
                    ...curseGroups.map((group) => ({
                      ...group,
                      value: String(group.value),
                    })),
                  ]}
                />
                {attachedGroup && (
                  <div className={classes.inputTrailingLabel}>
                    У Вас стоит автораспределение учащихся в данную группу,
                    отключите это в настройках курса, чтобы изменить группу!
                  </div>
                )}
              </div>
            )}
            {selectedTariff?.type !== 'free' && selectedTariff !== null && (
              <>
                <CheckBoxLabel
                  value={isNewPaymentForCourse}
                  onClick={() => {
                    setNewPaymentForCourse((prev) => !prev)
                  }}
                  label="Это новая оплата"
                  style={{ justifyContent: 'flex-start' }}
                />
                <div className={classes.inputWithLabel}>
                  <div className={classes.inputLabel}>Период</div>
                  <div className={classes.dateSelectionRow}>
                    <DatePicker
                      value={paidFromDate?.date}
                      onChange={onChangePaidFrom}
                      format={'YYYY.MM.DD'}
                      placeholder="С какого числа оплачен курс"
                      className={classes.datePicker}
                    />
                    <DatePicker
                      value={paidBeforeDate?.date}
                      onChange={onChangePaidBeforeDate}
                      format={'YYYY.MM.DD'}
                      placeholder="До какого числа оплачен курс"
                      className={classes.datePicker}
                    />
                  </div>
                </div>
                <Tooltip
                  title={
                    'Сумма, которую заплатили учащиеся за доступ к курсу, одинаковая для всех!'
                  }
                >
                  {/* @ts-ignore */}
                  <InputNew
                    className={classes.input}
                    title="Сумма оплаты"
                    value={paymentAmount ?? 0}
                    type="number"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      if (!isUniversalBalance) {
                        setPaymentAmount(Number(e.target.value))
                      }
                    }}
                    placeholder={
                      isUniversalBalance
                        ? 0
                        : String(
                            Number(selectedTariff?.first_price ?? 1300) * 3,
                          )
                    }
                  />
                </Tooltip>
                {isManualPaymentTypeSelectionEnabled && (
                  <div className={classes.inputWithLabel}>
                    <div className={classes.inputLabel}>Тип ручного платежа</div>
                    <Select
                      showSearch
                      allowClear
                      className={classes.select}
                      placeholder="Не выбрано"
                      optionFilterProp="label"
                      value={manualPaymentType}
                      onChange={setManualPaymentType}
                      filterSort={filterSort}
                      options={manualPaymentTypes.map((typeName) => ({
                        label: typeName,
                        value: typeName,
                      }))}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <CreateLearningGroup
          isVisible={createGroupModal}
          directionId={selectedCourse?.id ?? 0}
          onDismiss={() => setCreateGroupModal(false)}
          onCreate={onGroupCreated}
        />
      </>
    )
  },
)
SetCourseModalBody.displayName = 'SetCourseModalBody'

export default SetCourseModalBody
